import { css, useTheme } from '@emotion/react';
import {
  WPM_DEFAULT,
  calculateDurationMs,
  calculateTimeline,
  countWords,
  formatDuration,
  insertSectionBreaks,
  parseSections,
} from '@sonematic/shared';
import { $getRoot, type LexicalEditor } from 'lexical';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AudioLines, ChevronLeft, ChevronRight, Music, Sparkles } from 'lucide-react';

import { ConfirmRehearsalDialog } from '@/components/editor/confirm-rehearsal-dialog';
import { EditorSkeleton } from '@/components/editor/editor-skeleton';
import { $isSectionDividerNode } from '@/components/editor/lexical/section-divider-node';
import { markdownToEditorState } from '@/components/editor/lexical/serialization';
import { MusicPlayer } from '@/components/editor/music-player';
import { MusicSettingsPanel } from '@/components/editor/music-settings-panel';
import { ExportDialog } from '@/components/editor/export-dialog';
import type { ExportSection } from '@/components/editor/export-dialog';
import { NextStepCard } from '@/components/editor/next-step-card';
import { ScriptEditor } from '@/components/editor/script-editor';
import { SkipRehearsalCard } from '@/components/editor/skip-rehearsal-card';
import { deriveWorkflowStep } from '@/components/editor/workflow-utils';
import { WorkflowStepBar } from '@/components/editor/workflow-step-bar';
import type { DisplaySection } from '@/components/editor/section-preview';
import { ScriptTitle } from '@/components/editor/script-title';
import { SectionPreview } from '@/components/editor/section-preview';
import { SessionTransportControls } from '@/components/editor/session-transport-controls';
import { TimelineVisualization } from '@/components/editor/timeline-visualization';
import { ProgressPanel } from '@/components/generation/progress-panel';
import { ConnectDeviceDialog } from '@/components/sync/connect-device-dialog';
import { AppErrorPage } from '@/components/ui/app-error-page';
import { useAutoSave } from '@/hooks/use-auto-save';
import { useInspectorPanel } from '@/hooks/use-inspector-panel';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useGenerationStatus } from '@/hooks/use-generation-status';
import { useSyncSession } from '@/hooks/use-sync-session';
import {
  analyzeProject,
  cancelGeneration,
  fetchGenerationStatus,
  fetchProject,
  fetchUsage,
  getLatestRehearsal,
  retrySection,
  startGeneration,
  updateProject,
  updateProjectGlobalWpm,
  updateProjectMusicSettings,
  updateSectionMusicSettings,
  updateSectionTone,
  updateSectionWpm,
} from '@/lib/api-client';
import type {
  GenerationPartitionStatus,
  GenerationSectionStatus,
  ProjectSection,
  Rehearsal,
  UsageData,
} from '@/lib/api-client';
import { deriveTitleFromContent, isDefaultTitle } from '@/lib/utils/derive-title';
import { toFriendlyError } from '@/lib/utils/error-handler';
import { fontSizes, layout, mq, spacing } from '@/theme/tokens';

type SectionsSource = 'parser' | 'analyzed';

function serverSectionsToDisplay(sections: ProjectSection[]): DisplaySection[] {
  return sections.map((s) => ({
    position: s.position,
    title: `Section ${s.position + 1}`,
    content: s.content,
    wordCount: s.wordCount ?? countWords(s.content),
    detectedTone: s.detectedTone,
    toneConfidence: s.toneConfidence,
    speakerNotes: [],
  }));
}

export function ProjectEdit() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const connectParam = searchParams.get('connect') === 'true';
  const theme = useTheme();
  const { handleError } = useErrorHandler();
  const editorRef = useRef<LexicalEditor | null>(null);
  const {
    isOpen: inspectorOpen,
    isMobile,
    close: closeInspector,
    panelProps,
    edgeTabProps,
    backdropProps,
  } = useInspectorPanel();
  const [showTabHint, setShowTabHint] = useState(
    () => !localStorage.getItem('sonematic-inspector-tab-seen'),
  );
  const [rawContent, setRawContent] = useState('');
  const [title, setTitle] = useState('');
  const [globalWpm, setGlobalWpm] = useState(WPM_DEFAULT);
  const [positiveGlobalStyles, setPositiveGlobalStyles] = useState<string[]>([]);
  const [negativeGlobalStyles, setNegativeGlobalStyles] = useState<string[]>([]);
  const [energy, setEnergy] = useState<number | null>(null);
  const [aiSettingsModified, setAiSettingsModified] = useState(true);
  const [sectionWpmOverrides, setSectionWpmOverrides] = useState<Map<number, number | null>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [sectionsSource, setSectionsSource] = useState<SectionsSource>('parser');
  const [serverSections, setServerSections] = useState<ProjectSection[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [generationActive, setGenerationActive] = useState(false);
  const [latestRehearsal, setLatestRehearsal] = useState<Rehearsal | null>(null);
  const durationSource = latestRehearsal ? 'rehearsal' : 'wpm';
  const [connectDeviceOpen, setConnectDeviceOpen] = useState(connectParam);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [musicGenerated, setMusicGenerated] = useState(false);
  const [generatedSections, setGeneratedSections] = useState<GenerationSectionStatus[]>([]);
  const [generatedPartitions, setGeneratedPartitions] = useState<GenerationPartitionStatus[]>([]);
  const [confirmRehearsalOpen, setConfirmRehearsalOpen] = useState(false);
  const [sectionsGroupOpen, setSectionsGroupOpen] = useState(false);
  const [showSkipRehearsal, setShowSkipRehearsal] = useState(false);
  const [rehearsalSkipped, setRehearsalSkipped] = useState(false);
  const [sectionsAccepted, setSectionsAccepted] = useState(false);
  const [collapseAllToggle, setCollapseAllToggle] = useState(false);
  const [allSectionsCollapsed, setAllSectionsCollapsed] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [advancedDirty, setAdvancedDirty] = useState(false);
  const [localStyleOverrides, setLocalStyleOverrides] = useState<
    Map<number, { positive: string[] | null; negative: string[] | null }>
  >(new Map());
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const aiSuggestedValuesRef = useRef<{
    positiveGlobalStyles: string[];
    negativeGlobalStyles: string[];
    energy: number | null;
  } | null>(null);
  const titleManuallySet = useRef(false);
  const syncSession = useSyncSession();
  const sessionActive = syncSession.connectionState === 'active';

  useEffect(() => {
    if (connectParam) setConnectDeviceOpen(true);
  }, [connectParam]);

  useEffect(() => {
    if (showTabHint) {
      const timer = setTimeout(() => {
        localStorage.setItem('sonematic-inspector-tab-seen', 'true');
        setShowTabHint(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showTabHint]);

  // Fetch usage data for limit warnings
  useEffect(() => {
    fetchUsage()
      .then(setUsageData)
      .catch(() => {
        // Usage data is optional — ignore failures
      });
  }, []);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchProject(id)
      .then(async (project) => {
        if (cancelled) return;
        setRawContent(project.rawContent ?? '');
        setTitle(project.title);
        titleManuallySet.current = !isDefaultTitle(project.title);
        setGlobalWpm(project.globalWpm ?? WPM_DEFAULT);
        setPositiveGlobalStyles(project.positiveGlobalStyles ?? []);
        setNegativeGlobalStyles(project.negativeGlobalStyles ?? []);
        setEnergy(project.energy);
        aiSuggestedValuesRef.current = {
          positiveGlobalStyles: project.aiSuggestedPositiveGlobalStyles ?? [],
          negativeGlobalStyles: project.aiSuggestedNegativeGlobalStyles ?? [],
          energy: project.aiSuggestedEnergy,
        };
        setRehearsalSkipped(project.rehearsalSkipped ?? false);
        setSectionsAccepted(project.sectionsAccepted ?? false);
        const overrides = new Map<number, number | null>();
        for (const section of project.sections) {
          if (section.wpmOverride !== null) {
            overrides.set(section.position, section.wpmOverride);
          }
        }
        setSectionWpmOverrides(overrides);

        // Initialize per-section style overrides
        const sectionStyleOverrides = new Map<
          number,
          { positive: string[] | null; negative: string[] | null }
        >();
        for (const section of project.sections) {
          if (section.positiveLocalStyles || section.negativeLocalStyles) {
            sectionStyleOverrides.set(section.position, {
              positive: section.positiveLocalStyles,
              negative: section.negativeLocalStyles,
            });
          }
        }
        if (sectionStyleOverrides.size > 0) {
          setLocalStyleOverrides(sectionStyleOverrides);
          setAdvancedDirty(true);
        }

        setServerSections(project.sections);
        if (project.sections.length > 0) {
          setSectionsSource('analyzed');
        }

        // Check for active generation
        try {
          const genResult = await fetchGenerationStatus(id);
          if (cancelled) return;
          if (genResult.generation) {
            if (
              ['pending', 'tone_analysis', 'generating_music'].includes(genResult.generation.status)
            ) {
              setGenerationActive(true);
            } else if (genResult.generation.status === 'completed') {
              setMusicGenerated(true);
              setGeneratedSections(genResult.generation.sections);
              setGeneratedPartitions(genResult.generation.partitions);
            }
          }
        } catch {
          // Ignore — generation status is optional on load
        }

        // Check for rehearsal timings
        try {
          const rehearsal = await getLatestRehearsal(id);
          if (cancelled) return;
          if (rehearsal) {
            setLatestRehearsal(rehearsal);
          }
        } catch {
          // Ignore — rehearsal data is optional
        }

        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const friendly = toFriendlyError(err);
        setError({ title: friendly.title, message: friendly.message });
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const { saveStatus, saveNow } = useAutoSave({
    projectId: id!,
    rawContent,
    onSaveError: handleError,
  });

  // Auto-generate title from content when title hasn't been manually edited
  useEffect(() => {
    if (saveStatus !== 'saved') return;
    if (titleManuallySet.current) return;
    if (!isDefaultTitle(title)) return;
    const derived = deriveTitleFromContent(rawContent);
    if (derived) {
      setTitle(derived);
    }
  }, [saveStatus, rawContent, title]);

  const handleTitleManualEdit = useCallback(() => {
    titleManuallySet.current = true;
  }, []);

  // Keyboard shortcuts: Cmd/Ctrl+S to save, Cmd/Ctrl+E to export
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      if (e.key === 's') {
        e.preventDefault();
        saveNow();
      } else if (e.key === 'e') {
        e.preventDefault();
        setExportDialogOpen(true);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveNow]);

  const parseResult = useMemo(() => parseSections(rawContent), [rawContent]);
  const skipSourceResetRef = useRef(false);

  const isSingleSection = parseResult.sections.length === 1 && sectionsSource !== 'analyzed';

  const sectionsStale =
    sectionsSource === 'analyzed' &&
    serverSections.length > 0 &&
    parseResult.sections.length !== serverSections.length;

  const baseSections: DisplaySection[] = useMemo(() => {
    if (sectionsSource === 'analyzed' && serverSections.length > 0 && !sectionsStale) {
      return serverSectionsToDisplay(serverSections);
    }
    return parseResult.sections;
  }, [sectionsSource, serverSections, parseResult, sectionsStale]);

  const timelineData = useMemo(() => {
    if (durationSource === 'rehearsal' && latestRehearsal) {
      const rehearsalMap = new Map(
        latestRehearsal.sectionTimings.map((t) => [t.position, t.durationMs]),
      );
      const sections = baseSections.map((s) => ({
        position: s.position,
        title: s.title,
        durationMs:
          rehearsalMap.get(s.position) ??
          calculateDurationMs(s.wordCount, sectionWpmOverrides.get(s.position) ?? globalWpm),
        tone: s.detectedTone,
      }));
      const totalDurationMs = sections.reduce((sum, s) => sum + s.durationMs, 0);
      return { totalDurationMs, sections };
    }

    const timelineInput = baseSections.map((s) => ({
      wordCount: s.wordCount,
      wpm: sectionWpmOverrides.get(s.position) ?? globalWpm,
      position: s.position,
    }));
    const { totalDurationMs } = calculateTimeline(timelineInput);
    const sections = baseSections.map((s) => ({
      position: s.position,
      title: s.title,
      durationMs: calculateDurationMs(
        s.wordCount,
        sectionWpmOverrides.get(s.position) ?? globalWpm,
      ),
      tone: s.detectedTone,
    }));
    return { totalDurationMs, sections };
  }, [baseSections, globalWpm, sectionWpmOverrides, durationSource, latestRehearsal]);

  const totalDuration = timelineData.totalDurationMs;

  const handleEditorReady = useCallback((editor: LexicalEditor) => {
    editorRef.current = editor;
  }, []);

  const handleSectionClick = useCallback((position: number) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.update(() => {
      const root = $getRoot();
      const children = root.getChildren();

      let sectionIndex = 0;
      for (const child of children) {
        if (sectionIndex === position) {
          // Found the target section — select start of this node
          child.selectStart();
          // Scroll the DOM element into view
          const key = child.getKey();
          const element = editor.getElementByKey(key);
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
        if ($isSectionDividerNode(child)) {
          sectionIndex++;
        }
      }
    });
  }, []);

  const handleChange = useCallback((value: string) => {
    setRawContent(value);
    if (!skipSourceResetRef.current) {
      setSectionsSource('parser');
    }
    skipSourceResetRef.current = false;
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!id || analyzing) return;

    setAnalyzing(true);
    try {
      const result = await analyzeProject(id);
      setServerSections(result.sections);
      setSectionsSource('analyzed');
      setSectionsAccepted(false);

      // Apply AI music suggestions
      if (result.musicSuggestions) {
        setPositiveGlobalStyles(result.musicSuggestions.positiveGlobalStyles);
        setNegativeGlobalStyles(result.musicSuggestions.negativeGlobalStyles);
        setEnergy(result.musicSuggestions.energy);
        setAiSettingsModified(false);
        aiSuggestedValuesRef.current = {
          positiveGlobalStyles: result.musicSuggestions.positiveGlobalStyles,
          negativeGlobalStyles: result.musicSuggestions.negativeGlobalStyles,
          energy: result.musicSuggestions.energy,
        };
      }

      if (result.sections.length > 1 && editorRef.current) {
        const sectionContents = result.sections.map((s) => s.content);
        const newRawContent = insertSectionBreaks(rawContent, sectionContents);
        skipSourceResetRef.current = true;
        setRawContent(newRawContent);
        editorRef.current.update(() => {
          $getRoot().clear();
          markdownToEditorState(newRawContent);
        });
      }
    } catch (err) {
      handleError(err);
    } finally {
      setAnalyzing(false);
    }
  }, [id, analyzing, handleError, rawContent]);

  const handleAcceptSections = useCallback(async () => {
    if (!id) return;
    setSectionsAccepted(true);
    try {
      await updateProject(id, { sectionsAccepted: true });
    } catch {
      // Optimistic — local state already advanced; persist silently
    }
  }, [id]);

  const handleSkipRehearsal = useCallback(
    async (wpm: number) => {
      if (!id) return;
      setGlobalWpm(wpm);
      setRehearsalSkipped(true);
      setShowSkipRehearsal(false);
      try {
        await updateProjectGlobalWpm(id, wpm);
        await updateProject(id, { rehearsalSkipped: true });
      } catch (err) {
        handleError(err);
      }
    },
    [id, handleError],
  );

  const handleSectionWpmChange = useCallback(
    (position: number, wpmOverride: number | null) => {
      setSectionWpmOverrides((prev) => {
        const next = new Map(prev);
        if (wpmOverride === null) {
          next.delete(position);
        } else {
          next.set(position, wpmOverride);
        }
        return next;
      });
      if (id) {
        updateSectionWpm(id, position, wpmOverride).catch((err) => handleError(err));
      }
    },
    [id, handleError],
  );

  const handleSectionToneChange = useCallback(
    (position: number, tone: string | null) => {
      setServerSections((prev) =>
        prev.map((s) =>
          s.position === position ? { ...s, detectedTone: tone, toneConfidence: null } : s,
        ),
      );
      if (id && tone) {
        updateSectionTone(id, position, tone).catch((err) => handleError(err));
      }
    },
    [id, handleError],
  );

  const energyDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleEnergyChange = useCallback(
    (value: number) => {
      setEnergy(value);
      setAiSettingsModified(true);
      if (id) {
        if (energyDebounceRef.current) clearTimeout(energyDebounceRef.current);
        energyDebounceRef.current = setTimeout(() => {
          updateProjectMusicSettings(id, { energy: value }).catch((err) => handleError(err));
        }, 300);
      }
    },
    [id, handleError],
  );

  const { generation } = useGenerationStatus({
    projectId: id!,
    enabled: generationActive,
  });

  // Enrich base sections with generation status
  const displaySections: DisplaySection[] = useMemo(() => {
    if (generation?.sections) {
      const statusMap = new Map(generation.sections.map((s) => [s.position, s.generationStatus]));
      return baseSections.map((s) => ({
        ...s,
        generationStatus: statusMap.get(s.position) ?? s.generationStatus,
      }));
    }
    return baseSections;
  }, [baseSections, generation]);

  const handleGenerateConfirmed = useCallback(async () => {
    if (!id) return;
    try {
      await startGeneration(id);
      setGenerationActive(true);
    } catch (err) {
      handleError(err);
    }
  }, [id, handleError]);

  const handleGenerate = useCallback(() => {
    if (!latestRehearsal) {
      setConfirmRehearsalOpen(true);
      return;
    }
    handleGenerateConfirmed();
  }, [latestRehearsal, handleGenerateConfirmed]);

  const handleCancel = useCallback(async () => {
    if (!id) return;
    try {
      await cancelGeneration(id);
      setGenerationActive(false);
    } catch (err) {
      handleError(err);
    }
  }, [id, handleError]);

  const handleRetry = useCallback(
    async (sectionId: string) => {
      if (!id) return;
      try {
        await retrySection(id, sectionId);
      } catch (err) {
        handleError(err);
      }
    },
    [id, handleError],
  );

  const handleGenerationDone = useCallback(() => {
    const wasCompleted = generation?.status === 'completed';
    setGenerationActive(false);
    if (wasCompleted) {
      setMusicGenerated(true);
    }
    if (id) {
      fetchProject(id)
        .then((project) => {
          setServerSections(project.sections);
          setSectionsSource('analyzed');
        })
        .catch((err) => handleError(err));
      if (wasCompleted) {
        fetchGenerationStatus(id)
          .then((result) => {
            if (result.generation?.sections) {
              setGeneratedSections(result.generation.sections);
              setGeneratedPartitions(result.generation.partitions);
            }
          })
          .catch((err) => handleError(err));
      }
    }
  }, [id, handleError, generation?.status]);

  const hasAnalyzedSections = serverSections.some((s) => s.detectedTone !== null);
  const usagePercent =
    usageData?.limits.maxMonthlyMinutes != null && usageData.limits.maxMonthlyMinutes > 0
      ? (usageData.generatedMinutesThisMonth / usageData.limits.maxMonthlyMinutes) * 100
      : 0;
  const usageAtLimit = usagePercent >= 100;
  const usageWarning = usagePercent >= 80 && !usageAtLimit;
  const canGenerate =
    hasAnalyzedSections && positiveGlobalStyles.length > 0 && !generationActive && !usageAtLimit;

  const workflowStep = deriveWorkflowStep({
    rawContent,
    hasAnalyzedSections,
    sectionsStale,
    sectionsAccepted,
    hasRehearsal: !!latestRehearsal,
    rehearsalSkipped,
    generationActive,
    musicGenerated,
  });

  const [viewingStep, setViewingStep] = useState(workflowStep);

  // Auto-follow when workflow advances
  useEffect(() => {
    setViewingStep(workflowStep);
  }, [workflowStep]);

  const handleToggleAdvancedStyles = useCallback(() => {
    setAdvancedMode((prev) => !prev);
  }, []);

  const handleGlobalPositiveChange = useCallback(
    (styles: string[]) => {
      setPositiveGlobalStyles(styles);
      setAdvancedDirty(true);
      setAiSettingsModified(true);
      if (id) {
        updateProjectMusicSettings(id, {
          positiveGlobalStyles: styles,
        }).catch((err) => handleError(err));
      }
    },
    [id, handleError],
  );

  const handleGlobalNegativeChange = useCallback(
    (styles: string[]) => {
      setNegativeGlobalStyles(styles);
      setAdvancedDirty(true);
      setAiSettingsModified(true);
      if (id) {
        updateProjectMusicSettings(id, {
          negativeGlobalStyles: styles,
        }).catch((err) => handleError(err));
      }
    },
    [id, handleError],
  );

  const handleSectionPositiveChange = useCallback(
    (position: number, styles: string[]) => {
      setLocalStyleOverrides((prev) => {
        const next = new Map(prev);
        const existing = next.get(position) ?? { positive: null, negative: null };
        next.set(position, { ...existing, positive: styles });
        return next;
      });
      setAdvancedDirty(true);
      if (id) {
        updateSectionMusicSettings(id, position, {
          positiveLocalStyles: styles,
        }).catch((err) => handleError(err));
      }
    },
    [id, handleError],
  );

  const handleSectionNegativeChange = useCallback(
    (position: number, styles: string[]) => {
      setLocalStyleOverrides((prev) => {
        const next = new Map(prev);
        const existing = next.get(position) ?? { positive: null, negative: null };
        next.set(position, { ...existing, negative: styles });
        return next;
      });
      setAdvancedDirty(true);
      if (id) {
        updateSectionMusicSettings(id, position, {
          negativeLocalStyles: styles,
        }).catch((err) => handleError(err));
      }
    },
    [id, handleError],
  );

  const handleResetToSuggested = useCallback(() => {
    const suggested = aiSuggestedValuesRef.current;
    if (!suggested) return;

    setPositiveGlobalStyles(suggested.positiveGlobalStyles);
    setNegativeGlobalStyles(suggested.negativeGlobalStyles);
    setEnergy(suggested.energy);
    setAiSettingsModified(false);

    // Also reset advanced mode
    setLocalStyleOverrides(new Map());
    setAdvancedDirty(false);
    setAdvancedMode(false);

    if (id) {
      updateProjectMusicSettings(id, {
        positiveGlobalStyles: suggested.positiveGlobalStyles,
        negativeGlobalStyles: suggested.negativeGlobalStyles,
        energy: suggested.energy,
      }).catch((err) => handleError(err));
    }
  }, [id, handleError]);

  const advancedSections = useMemo(
    () =>
      displaySections.map((s) => {
        const overrides = localStyleOverrides.get(s.position);
        return {
          position: s.position,
          name: `Section ${s.position + 1}`,
          duration: formatDuration(
            calculateDurationMs(s.wordCount, sectionWpmOverrides.get(s.position) ?? globalWpm),
          ),
          positiveLocalStyles: overrides?.positive ?? [],
          negativeLocalStyles: overrides?.negative ?? [],
        };
      }),
    [displaySections, localStyleOverrides, sectionWpmOverrides, globalWpm],
  );

  // Session transport state (for driver controlling follower)
  const [sessionSectionIndex, setSessionSectionIndex] = useState(0);
  const [sessionPlaying, setSessionPlaying] = useState(false);

  const handleSessionPlayPause = useCallback(() => {
    setSessionPlaying((prev) => {
      const next = !prev;
      syncSession.socket?.emit(next ? 'transport:play' : 'transport:pause', {
        timeMs: 0,
        sectionIndex: sessionSectionIndex,
      });
      return next;
    });
  }, [syncSession.socket, sessionSectionIndex]);

  const handleSessionPrevSection = useCallback(() => {
    setSessionSectionIndex((prev) => {
      const next = Math.max(0, prev - 1);
      syncSession.socket?.emit('transport:seek', {
        timeMs: 0,
        sectionIndex: next,
      });
      return next;
    });
  }, [syncSession.socket]);

  const handleSessionNextSection = useCallback(() => {
    const maxSection = baseSections.length - 1;
    setSessionSectionIndex((prev) => {
      const next = Math.min(maxSection, prev + 1);
      syncSession.socket?.emit('transport:seek', {
        timeMs: 0,
        sectionIndex: next,
      });
      return next;
    });
  }, [syncSession.socket, baseSections.length]);

  const handleEndSession = useCallback(() => {
    syncSession.leaveSession();
    setSessionPlaying(false);
    setSessionSectionIndex(0);
    setConnectDeviceOpen(false);
  }, [syncSession]);

  if (loading) {
    return <EditorSkeleton />;
  }

  if (error) {
    return (
      <AppErrorPage
        title={error.title}
        message={error.message}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div
      css={css`
        padding: ${spacing.lg};
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto auto 1fr auto;
        height: calc(100dvh - 44px);
        overflow: hidden;

        ${mq.lg} {
          height: 100dvh;
          grid-template-columns: ${inspectorOpen
            ? `1fr ${layout.inspectorWidth}`
            : `1fr ${layout.inspectorTabWidth}`};
          grid-template-rows: auto auto 1fr auto;
          gap: 0 ${inspectorOpen ? spacing.lg : '0'};
          ${!inspectorOpen ? 'padding-right: 0;' : ''}
          transition: grid-template-columns 0.3s ease;
        }

        ${mq.xl} {
          grid-template-columns: ${inspectorOpen
            ? `1fr ${layout.inspectorWidthXl}`
            : `1fr ${layout.inspectorTabWidth}`};
        }
      `}
    >
      <nav
        aria-label="Breadcrumb"
        css={css`
          font-size: ${fontSizes.xs};
          margin-bottom: ${spacing.xs};
          color: ${theme.colors.textSecondary};

          ${mq.lg} {
            grid-column: 1;
          }
        `}
      >
        <Link
          to="/app/projects"
          css={css`
            color: ${theme.colors.textSecondary};
            text-decoration: none;
            &:hover {
              color: ${theme.colors.primary};
            }
          `}
        >
          Scripts
        </Link>
        <ChevronRight
          size={12}
          aria-hidden="true"
          css={css`
            margin: 0 ${spacing.xs};
            color: ${theme.colors.textSecondary};
            flex-shrink: 0;
          `}
        />
        <span
          css={css`
            color: ${theme.colors.text};
          `}
        >
          {title}
        </span>
      </nav>
      <div
        css={css`
          margin-bottom: ${spacing.md};

          ${mq.lg} {
            grid-column: 1;
          }
        `}
      >
        <ScriptTitle projectId={id!} initialTitle={title} onManualEdit={handleTitleManualEdit} />
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          min-width: 0;
          min-height: 0;
          overflow: hidden;

          ${mq.lg} {
            grid-column: 1;
          }
        `}
      >
        <div
          css={css`
            flex: 1;
            min-height: 0;
            overflow: hidden;
          `}
        >
          <ScriptEditor
            initialContent={rawContent}
            onChange={handleChange}
            onEditorReady={handleEditorReady}
            wordCount={parseResult.totalWordCount}
            charCount={parseResult.totalCharCount}
            sectionCount={parseResult.sections.length}
            totalDuration={totalDuration > 0 ? formatDuration(totalDuration) : undefined}
            timingEstimated={!latestRehearsal && !rehearsalSkipped}
            saveStatus={saveStatus}
            readOnly={sessionActive}
            onExport={() => setExportDialogOpen(true)}
          />
        </div>
        {displaySections.length > 0 && (
          <div
            css={css`
              flex-shrink: 0;
              padding-top: ${spacing.sm};
            `}
          >
            <TimelineVisualization
              sections={timelineData.sections}
              totalDurationMs={timelineData.totalDurationMs}
              onSectionClick={handleSectionClick}
              timingEstimated={!latestRehearsal && !rehearsalSkipped}
            />
          </div>
        )}
      </div>
      {isMobile &&
        inspectorOpen &&
        createPortal(
          <div
            role="button"
            tabIndex={0}
            {...backdropProps}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') closeInspector();
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: theme.colors.overlay,
              zIndex: 9998,
            }}
          />,
          document.body,
        )}
      {/* Desktop edge tab removed — pull tab is now inside the panel for all viewports */}
      <div
        {...panelProps}
        css={css`
          min-height: 0;
          display: flex;
          flex-direction: column;
          background: ${theme.colors.background};

          ${isMobile
            ? `
              padding: ${spacing.md};
              box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
              overflow: visible;
            `
            : ''}

          ${mq.lg} {
            grid-column: 2;
            grid-row: 1 / -1;
            position: relative;
            ${inspectorOpen
              ? `
              border-left: 1px solid ${theme.colors.border};
              margin-top: -${spacing.lg};
              margin-bottom: -${spacing.lg};
              padding-left: ${spacing.md};
              padding-top: ${spacing.lg};
              padding-bottom: ${spacing.lg};
              `
              : `
              padding: 0;
              border: none;
              `}
          }
        `}
      >
        <button
          {...edgeTabProps}
          type="button"
          css={css`
            position: absolute;
            width: 18px;
            height: 96px;
            top: 50%;
            transform: translateY(-50%);
            ${isMobile
              ? `left: -18px;`
              : inspectorOpen
                ? `left: calc(-18px - 1px);`
                : `left: auto; right: 0;`}
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid ${theme.colors.border};
            border-right: none;
            border-radius: 10px 0 0 10px;
            background: ${theme.colors.surface};
            color: ${theme.colors.textSecondary};
            cursor: pointer;
            padding: 0;
            box-shadow: -2px 0 6px rgba(0, 0, 0, 0.08);
            transition:
              color 0.15s,
              background 0.15s;

            &:hover {
              color: ${theme.colors.text};
              background: ${theme.colors.surfaceElevated};
            }

            ${showTabHint && !inspectorOpen
              ? `
              animation: tabHint 0.6s ease 0.5s;
              @keyframes tabHint {
                0%, 100% { transform: translateY(-50%); }
                30% { transform: translateY(-50%) translateX(-8px); }
                60% { transform: translateY(-50%) translateX(4px); }
              }
            `
              : ''}
          `}
          title={edgeTabProps['aria-label']}
        >
          {inspectorOpen ? (
            <ChevronRight size={12} aria-hidden="true" />
          ) : (
            <ChevronLeft size={12} aria-hidden="true" />
          )}
        </button>
        <div
          css={css`
            display: ${!inspectorOpen && !isMobile ? 'none' : 'flex'};
            flex-direction: column;
            flex: 1;
            min-height: 0;
          `}
        >
          <div
            css={css`
              flex-shrink: 0;
            `}
          >
            <WorkflowStepBar
              currentStep={workflowStep}
              viewingStep={viewingStep}
              onStepClick={setViewingStep}
            />
            {(viewingStep === 'write' ||
              viewingStep === 'analyze' ||
              viewingStep === 'review' ||
              viewingStep === 'rehearse') &&
              !showSkipRehearsal && (
                <div
                  key={viewingStep}
                  css={css`
                    animation: fadeIn 0.15s ease-in;
                    @keyframes fadeIn {
                      from {
                        opacity: 0;
                      }
                      to {
                        opacity: 1;
                      }
                    }
                  `}
                >
                  <NextStepCard
                    step={viewingStep}
                    projectId={id!}
                    analyzing={analyzing}
                    disabled={!rawContent.trim()}
                    generationActive={generationActive}
                    hasRehearsal={!!latestRehearsal}
                    sectionCount={displaySections.length}
                    onAnalyze={handleAnalyze}
                    onGenerate={handleGenerate}
                    onAcceptSections={handleAcceptSections}
                    onSkipRehearsal={() => setShowSkipRehearsal(true)}
                  />
                </div>
              )}
            {viewingStep !== workflowStep && (
              <button
                type="button"
                onClick={() => setViewingStep(workflowStep)}
                css={css`
                  display: block;
                  width: 100%;
                  padding: 0;
                  margin: 0 0 ${spacing.md};
                  border: none;
                  background: none;
                  font-size: ${fontSizes.xs};
                  color: ${theme.colors.primary};
                  cursor: pointer;
                  text-align: center;

                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                Return to current step &rarr;
              </button>
            )}
            {viewingStep === 'rehearse' && showSkipRehearsal && (
              <SkipRehearsalCard
                scriptContent={rawContent}
                currentWpm={globalWpm}
                onSkip={handleSkipRehearsal}
                onCancel={() => setShowSkipRehearsal(false)}
              />
            )}
          </div>
          <div
            css={css`
              flex: 1;
              min-height: 0;
              overflow-y: auto;

              &::-webkit-scrollbar {
                width: 6px;
              }
              &::-webkit-scrollbar-track {
                background: transparent;
              }
              &::-webkit-scrollbar-thumb {
                background: ${theme.colors.border};
                border-radius: 3px;
              }
            `}
          >
            {['generate', 'complete'].includes(viewingStep) &&
              (generationActive && generation ? (
                <ProgressPanel
                  generation={generation}
                  onCancel={handleCancel}
                  onRetry={handleRetry}
                  onDone={handleGenerationDone}
                />
              ) : (
                <div
                  css={css`
                    border: 1px solid ${theme.colors.border};
                    border-radius: 12px;
                    background: ${theme.colors.surface};
                    padding: ${spacing.lg};
                    text-align: center;
                    margin-bottom: ${spacing.lg};
                  `}
                >
                  {musicGenerated && generatedSections.length > 0 ? (
                    <MusicPlayer
                      sections={generatedSections.map((gs) => {
                        const serverSection = serverSections.find(
                          (s) => s.position === gs.position,
                        );
                        return {
                          position: gs.position,
                          detectedTone: serverSection?.detectedTone ?? null,
                          generationStatus: gs.generationStatus,
                          audioUrl: gs.audioUrl,
                          audioDurationMs: gs.audioDurationMs,
                          audioOffsetMs: gs.audioOffsetMs ?? 0,
                          partitionIndex: gs.partitionIndex ?? 0,
                        };
                      })}
                      partitions={generatedPartitions}
                      totalDurationMs={generation?.totalDurationMs ?? 0}
                    />
                  ) : (
                    <>
                      <Music
                        size={40}
                        aria-hidden="true"
                        css={css`
                          color: ${theme.colors.primary};
                          margin-bottom: ${spacing.md};
                        `}
                      />
                      <h3
                        css={css`
                          margin: 0 0 ${spacing.sm};
                          font-size: ${fontSizes.md};
                          color: ${theme.colors.text};
                          font-weight: 600;
                        `}
                      >
                        Ready to Generate Music
                      </h3>
                      <p
                        css={css`
                          margin: 0;
                          font-size: ${fontSizes.sm};
                          color: ${theme.colors.textSecondary};
                          line-height: 1.5;
                        `}
                      >
                        {(() => {
                          const tones = serverSections
                            .filter((s) => s.detectedTone)
                            .map((s) => s.detectedTone!);
                          if (tones.length === 0)
                            return 'Review the music settings below, then click Generate.';
                          const counts = tones.reduce(
                            (acc, t) => ({ ...acc, [t]: (acc[t] || 0) + 1 }),
                            {} as Record<string, number>,
                          );
                          const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
                          return `Based on your speech's ${dominant} tone, we've configured music to complement your delivery.`;
                        })()}
                      </p>
                    </>
                  )}
                  <div
                    css={css`
                      text-align: left;
                      border-top: 1px solid ${theme.colors.border};
                      margin-top: ${spacing.md};
                      padding-top: ${spacing.md};
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        margin-bottom: ${spacing.md};
                      `}
                    >
                      <span
                        css={css`
                          font-size: ${fontSizes.sm};
                          font-weight: 600;
                          color: ${theme.colors.text};
                        `}
                      >
                        Music Settings
                      </span>
                      {advancedDirty || aiSettingsModified ? (
                        <button
                          type="button"
                          onClick={handleResetToSuggested}
                          css={css`
                            display: inline-flex;
                            align-items: center;
                            gap: 3px;
                            margin-left: auto;
                            font-size: ${fontSizes.xs};
                            color: ${theme.colors.info};
                            font-weight: 500;
                            background: none;
                            border: 1px solid rgba(96, 165, 250, 0.3);
                            border-radius: 6px;
                            padding: 2px 8px;
                            cursor: pointer;
                            &:hover {
                              background: rgba(96, 165, 250, 0.1);
                            }
                          `}
                        >
                          <Sparkles size={12} aria-hidden="true" />
                          Reset to Suggested
                        </button>
                      ) : (
                        <span
                          css={css`
                            display: inline-flex;
                            align-items: center;
                            gap: 3px;
                            margin-left: auto;
                            font-size: ${fontSizes.xs};
                            color: ${theme.colors.info};
                            font-weight: 500;
                          `}
                        >
                          <Sparkles size={12} aria-hidden="true" />
                          Suggested
                        </span>
                      )}
                    </div>
                    <MusicSettingsPanel
                      positiveGlobalStyles={positiveGlobalStyles}
                      negativeGlobalStyles={negativeGlobalStyles}
                      onPositiveGlobalStylesChange={handleGlobalPositiveChange}
                      onNegativeGlobalStylesChange={handleGlobalNegativeChange}
                      energy={energy}
                      onEnergyChange={handleEnergyChange}
                      advancedMode={advancedMode}
                      onToggleAdvanced={handleToggleAdvancedStyles}
                      advancedStylesProps={{
                        positiveGlobalStyles,
                        negativeGlobalStyles,
                        sections: advancedSections,
                        onGlobalPositiveChange: handleGlobalPositiveChange,
                        onGlobalNegativeChange: handleGlobalNegativeChange,
                        onSectionPositiveChange: handleSectionPositiveChange,
                        onSectionNegativeChange: handleSectionNegativeChange,
                      }}
                    />
                  </div>
                  {(usageWarning || usageAtLimit) && usageData && (
                    <div
                      data-testid="usage-warning-banner"
                      css={css`
                        margin-top: ${spacing.md};
                        padding: ${spacing.sm} ${spacing.md};
                        border-radius: 8px;
                        font-size: ${fontSizes.sm};
                        font-weight: 500;
                        background: ${usageAtLimit
                          ? theme.colors.errorMuted
                          : theme.colors.warningMuted};
                        color: ${usageAtLimit ? theme.colors.error : theme.colors.warning};
                      `}
                    >
                      {usageAtLimit
                        ? 'Generation limit reached. Resets on the 1st of each month.'
                        : `You've used ${usageData.generatedMinutesThisMonth} of ${usageData.limits.maxMonthlyMinutes} generation minutes this month`}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={!canGenerate}
                    title={!canGenerate ? 'Analyze your script first to generate music' : undefined}
                    css={css`
                      width: 100%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      gap: ${spacing.sm};
                      padding: ${musicGenerated
                        ? `${spacing.sm} ${spacing.lg}`
                        : `${spacing.sm} ${spacing.lg}`};
                      margin-top: ${spacing.md};
                      border-radius: 980px;
                      border: ${musicGenerated ? `1px solid ${theme.colors.border}` : 'none'};
                      background: ${musicGenerated ? 'transparent' : theme.colors.primary};
                      color: ${musicGenerated ? theme.colors.text : theme.colors.primaryText};
                      font-size: ${fontSizes.md};
                      font-weight: 600;
                      cursor: pointer;
                      transition: all 0.2s ease-in-out;

                      &:hover:not(:disabled) {
                        ${musicGenerated
                          ? `border-color: ${theme.colors.text};`
                          : `background: ${theme.colors.primaryHover};`}
                      }

                      &:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                      }
                    `}
                  >
                    <AudioLines size={16} aria-hidden="true" />
                    {musicGenerated ? 'Regenerate All Music' : 'Generate Music'}
                  </button>
                  <p
                    css={css`
                      margin: ${spacing.sm} 0 0;
                      font-size: ${fontSizes.xs};
                      color: ${theme.colors.textSecondary};
                    `}
                  >
                    This usually takes 2–3 minutes
                  </p>
                  {!canGenerate && !generationActive && (
                    <p
                      css={css`
                        margin: ${spacing.xs} 0 0;
                        font-size: ${fontSizes.xs};
                        color: ${theme.colors.warning};
                      `}
                    >
                      Analyze your script to enable generation
                    </p>
                  )}
                </div>
              ))}
            {viewingStep === 'review' && !isSingleSection && (
              <>
                <SectionPreview
                  sections={displaySections}
                  globalWpm={globalWpm}
                  sectionWpmOverrides={sectionWpmOverrides}
                  onSectionWpmChange={handleSectionWpmChange}
                  onSectionToneChange={
                    sectionsSource === 'analyzed' && !sectionsStale
                      ? handleSectionToneChange
                      : undefined
                  }
                  generatedSections={[]}
                  timingEstimated={!latestRehearsal && !rehearsalSkipped}
                  externalCollapseAll={collapseAllToggle}
                  onAllCollapsedChange={setAllSectionsCollapsed}
                  hideWpm
                />
              </>
            )}
            {viewingStep === 'complete' && !isSingleSection && (
              <>
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin: ${spacing.lg} 0 ${sectionsGroupOpen ? spacing.md : '0'};
                    border-top: 1px solid ${theme.colors.border};
                    padding-top: ${spacing.lg};
                  `}
                >
                  <button
                    type="button"
                    onClick={() => setSectionsGroupOpen(!sectionsGroupOpen)}
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${spacing.sm};
                      padding: 0;
                      border: none;
                      background: none;
                      cursor: pointer;
                      font-size: ${fontSizes.lg};
                      font-weight: 700;
                      color: ${theme.colors.text};
                    `}
                  >
                    <ChevronRight
                      size={14}
                      aria-hidden="true"
                      css={css`
                        transition: transform 0.15s ease;
                        transform: rotate(${sectionsGroupOpen ? '90deg' : '0deg'});
                        flex-shrink: 0;
                      `}
                    />
                    Sections
                  </button>
                  {sectionsGroupOpen && displaySections.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setCollapseAllToggle((prev) => (prev === undefined ? true : !prev))
                      }
                      css={css`
                        font-size: ${fontSizes.xs};
                        color: ${theme.colors.textSecondary};
                        background: transparent;
                        border: none;
                        cursor: pointer;
                        padding: ${spacing.xs} ${spacing.sm};
                        border-radius: 4px;
                        margin-left: auto;

                        &:hover {
                          color: ${theme.colors.text};
                          background: ${theme.colors.border};
                        }
                      `}
                    >
                      {allSectionsCollapsed ? 'Expand All' : 'Collapse All'}
                    </button>
                  )}
                </div>
                {sectionsGroupOpen && (
                  <>
                    {sectionsStale && (
                      <div
                        css={css`
                          margin: 0 0 ${spacing.md};
                          padding: ${spacing.sm} ${spacing.md};
                          background: ${theme.colors.warningMuted ?? theme.colors.errorMuted};
                          color: ${theme.colors.warning ?? theme.colors.error};
                          border-radius: 6px;
                          font-size: ${fontSizes.sm};
                          font-weight: 500;
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: ${spacing.sm};
                        `}
                      >
                        <span>Sections outdated — re-analyze to match current content</span>
                        <button
                          type="button"
                          onClick={handleAnalyze}
                          disabled={analyzing || !rawContent.trim()}
                          css={css`
                            font-size: ${fontSizes.xs};
                            color: ${theme.colors.primaryText};
                            background: ${theme.colors.warning ?? theme.colors.error};
                            padding: ${spacing.xs} ${spacing.sm};
                            border-radius: 4px;
                            border: none;
                            cursor: pointer;
                            font-weight: 600;
                            white-space: nowrap;
                            transition: opacity 0.15s ease;

                            &:hover:not(:disabled) {
                              opacity: 0.85;
                            }

                            &:disabled {
                              opacity: 0.5;
                              cursor: not-allowed;
                            }
                          `}
                        >
                          {analyzing ? 'Analyzing...' : 'Analyze Now'}
                        </button>
                      </div>
                    )}
                    <SectionPreview
                      sections={displaySections}
                      globalWpm={globalWpm}
                      sectionWpmOverrides={sectionWpmOverrides}
                      onSectionWpmChange={handleSectionWpmChange}
                      onSectionToneChange={
                        sectionsSource === 'analyzed' && !sectionsStale
                          ? handleSectionToneChange
                          : undefined
                      }
                      generatedSections={generatedSections}
                      partitions={generatedPartitions}
                      timingEstimated={!latestRehearsal && !rehearsalSkipped}
                      externalCollapseAll={collapseAllToggle}
                      onAllCollapsedChange={setAllSectionsCollapsed}
                      musicOnly={true}
                    />
                  </>
                )}
              </>
            )}
          </div>
          {sessionActive && displaySections.length > 0 && (
            <div
              css={css`
                padding-top: ${spacing.md};
                border-top: 1px solid ${theme.colors.border};
                margin-top: ${spacing.md};
              `}
            >
              <SessionTransportControls
                isPlaying={sessionPlaying}
                currentSection={sessionSectionIndex}
                sectionCount={baseSections.length}
                onPlayPause={handleSessionPlayPause}
                onPrevSection={handleSessionPrevSection}
                onNextSection={handleSessionNextSection}
                participants={syncSession.participants}
                sessionSettings={syncSession.sessionSettings}
                onMusicDeviceChange={(device) =>
                  syncSession.updateSettings({ musicDevice: device })
                }
                onMicDeviceChange={(device) => syncSession.updateSettings({ micDevice: device })}
                onEndSession={handleEndSession}
              />
            </div>
          )}
        </div>
      </div>
      {canGenerate && !generationActive && !inspectorOpen && (
        <div
          aria-hidden="true"
          css={css`
            display: block;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 5;
            padding: ${spacing.md};
            background: ${theme.colors.background};
            border-top: 1px solid ${theme.colors.border};

            ${mq.lg} {
              display: none;
            }
          `}
        >
          <button
            type="button"
            tabIndex={-1}
            onClick={handleGenerate}
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: ${spacing.sm};
              padding: ${spacing.sm} ${spacing.lg};
              border-radius: 980px;
              border: none;
              background: ${theme.colors.primary};
              color: ${theme.colors.primaryText};
              font-size: ${fontSizes.md};
              font-weight: 600;
              cursor: pointer;
              transition: background 0.2s ease-in-out;

              &:hover {
                background: ${theme.colors.primaryHover};
              }
            `}
          >
            <AudioLines size={16} aria-hidden="true" />
            {musicGenerated ? 'Regenerate All Music' : 'Generate Music'}
          </button>
        </div>
      )}
      <ConfirmRehearsalDialog
        isOpen={confirmRehearsalOpen}
        projectId={id!}
        onConfirm={() => {
          setConfirmRehearsalOpen(false);
          handleGenerateConfirmed();
        }}
        onCancel={() => setConfirmRehearsalOpen(false)}
      />
      <ConnectDeviceDialog
        isOpen={connectDeviceOpen}
        onClose={() => {
          setConnectDeviceOpen(false);
          if (searchParams.has('connect')) {
            searchParams.delete('connect');
            setSearchParams(searchParams, { replace: true });
          }
        }}
        projectId={id!}
        session={syncSession}
      />
      <ExportDialog
        isOpen={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        title={title}
        rawContent={rawContent}
        sections={displaySections.map(
          (s): ExportSection => ({
            position: s.position,
            title: s.title,
            content: s.content,
            wordCount: s.wordCount,
            estimatedDurationMs: calculateDurationMs(s.wordCount, globalWpm),
          }),
        )}
      />
    </div>
  );
}
